import type { ImagePickerAsset } from 'expo-image-picker';

import type { GiphyGif } from '@/src/features/giphy/giphy-service';
import type {
  CreateGarageVehicleCommentInput,
  GarageVehicleComment,
} from '@/src/features/garage/garage-service';
import type {
  CommentMedia,
  CommentReactionCounts,
  CommentUploadMediaKind,
  CreatePostCommentInput,
  PostComment,
} from '@/src/features/posts/post-service';

export type OptimisticCommentAuthor = {
  authorFounderBadge?: boolean;
  authorFullName?: string | null;
  authorPhotoURL?: string | null;
  authorUsername?: string | null;
  authorVerifiedBadge?: boolean;
};

type OptimisticPostCommentInput = CreatePostCommentInput &
  OptimisticCommentAuthor;
type OptimisticGarageCommentInput = CreateGarageVehicleCommentInput &
  OptimisticCommentAuthor;
type ReconciledComment = {
  authorUid: string;
  body: string | null;
  createdAt?: Date | { toMillis: () => number } | string | null;
  localCreatedAtMillis?: number;
  parentCommentId: string | null;
  replyToCommentId: string | null;
};

const SERVER_MATCH_CLOCK_SKEW_MS = 2_000;
const SERVER_MATCH_CONFIRMATION_WINDOW_MS = 120_000;

function optimisticCommentId(targetId: string) {
  return `optimistic:${targetId}:${Date.now()}:${Math.random()
    .toString(36)
    .slice(2)}`;
}

function emptyOptimisticCommentReactionCounts(): CommentReactionCounts {
  return {
    dislike: 0,
    fire: 0,
    laugh: 0,
    like: 0,
    love: 0,
    trash: 0,
    vomit: 0,
    wow: 0,
  };
}

function buildGiphyMedia(gif: GiphyGif): CommentMedia {
  return {
    giphyId: gif.giphyId,
    height: gif.height,
    kind: 'gif',
    originalURL: gif.originalURL,
    pageURL: gif.pageURL,
    previewURL: gif.previewURL,
    source: 'giphy',
    title: gif.title,
    width: gif.width,
  };
}

function buildLocalUploadMedia({
  id,
  mediaAsset,
  mediaKind,
}: {
  id: string;
  mediaAsset: ImagePickerAsset | null | undefined;
  mediaKind: CommentUploadMediaKind | null | undefined;
}): CommentMedia | null {
  if (!mediaAsset || !mediaKind) {
    return null;
  }

  return {
    contentType:
      mediaAsset.mimeType ?? (mediaKind === 'image' ? 'image/jpeg' : 'video/mp4'),
    downloadURL: mediaAsset.uri,
    durationMillis: mediaKind === 'video' ? (mediaAsset.duration ?? null) : null,
    height: mediaAsset.height ?? 0,
    kind: mediaKind,
    sizeBytes: mediaAsset.fileSize ?? 0,
    source: 'upload',
    storagePath: `local/${id}`,
    thumbnail: null,
    thumbnailStatus: 'queued',
    width: mediaAsset.width ?? 0,
  };
}

function buildOptimisticMedia({
  giphyGif,
  id,
  mediaAsset,
  mediaKind,
}: {
  giphyGif: GiphyGif | null | undefined;
  id: string;
  mediaAsset: ImagePickerAsset | null | undefined;
  mediaKind: CommentUploadMediaKind | null | undefined;
}) {
  return giphyGif
    ? buildGiphyMedia(giphyGif)
    : buildLocalUploadMedia({ id, mediaAsset, mediaKind });
}

export function buildOptimisticPostComment(
  input: OptimisticPostCommentInput,
): PostComment {
  const id = optimisticCommentId(input.postId);
  const createdAt = new Date();

  return {
    authorFounderBadge: input.authorFounderBadge,
    authorFullName: input.authorFullName ?? null,
    authorPhotoURL: input.authorPhotoURL ?? null,
    authorUid: input.authorUid,
    authorUsername: input.authorUsername ?? null,
    authorVerifiedBadge: input.authorVerifiedBadge,
    body: input.body.trim() || null,
    createdAt,
    editCount: 0,
    editedAt: null,
    id,
    linkPreview: null,
    localCreatedAtMillis: createdAt.getTime(),
    media: buildOptimisticMedia({
      giphyGif: input.giphyGif,
      id,
      mediaAsset: input.mediaAsset,
      mediaKind: input.mediaKind,
    }),
    parentCommentId: input.parentCommentId ?? null,
    profileMentions: input.profileMentions ?? [],
    reactionCount: 0,
    reactionCounts: emptyOptimisticCommentReactionCounts(),
    replyToCommentId: input.replyToCommentId ?? null,
    replyToFullName: null,
    replyToUid: null,
    replyToUsername: null,
    syncStatus: 'uploading',
    updatedAt: createdAt,
  };
}

export function buildOptimisticGarageVehicleComment(
  input: OptimisticGarageCommentInput,
): GarageVehicleComment {
  const id = optimisticCommentId(input.vehicleId);
  const createdAt = new Date();

  return {
    authorFounderBadge: input.authorFounderBadge,
    authorFullName: input.authorFullName ?? null,
    authorPhotoURL: input.authorPhotoURL ?? null,
    authorUid: input.authorUid,
    authorUsername: input.authorUsername ?? null,
    authorVerifiedBadge: input.authorVerifiedBadge,
    body: input.body.trim(),
    createdAt,
    editCount: 0,
    editedAt: null,
    id,
    linkPreview: null,
    localCreatedAtMillis: createdAt.getTime(),
    media: buildOptimisticMedia({
      giphyGif: input.giphyGif,
      id,
      mediaAsset: input.mediaAsset,
      mediaKind: input.mediaKind,
    }),
    parentCommentId: input.parentCommentId ?? null,
    profileMentions: input.profileMentions ?? [],
    reactionCount: 0,
    reactionCounts: emptyOptimisticCommentReactionCounts(),
    replyToCommentId: input.replyToCommentId ?? null,
    replyToFullName: null,
    replyToUid: null,
    replyToUsername: null,
    syncStatus: 'uploading',
    updatedAt: createdAt,
  };
}

function commentCreatedAtMillis(comment: ReconciledComment) {
  if (comment.createdAt instanceof Date) {
    return comment.createdAt.getTime();
  }

  if (typeof comment.createdAt === 'string') {
    return Date.parse(comment.createdAt);
  }

  return comment.createdAt?.toMillis?.() ?? Number.NaN;
}

export function serverCommentMatchesOptimisticComment({
  optimistic,
  server,
}: {
  optimistic: ReconciledComment;
  server: ReconciledComment;
}) {
  const optimisticCreatedAtMillis = optimistic.localCreatedAtMillis;
  const serverCreatedAtMillis = commentCreatedAtMillis(server);

  if (
    !optimisticCreatedAtMillis ||
    !Number.isFinite(serverCreatedAtMillis)
  ) {
    return false;
  }

  const earliestMatch =
    optimisticCreatedAtMillis - SERVER_MATCH_CLOCK_SKEW_MS;
  const latestMatch =
    optimisticCreatedAtMillis + SERVER_MATCH_CONFIRMATION_WINDOW_MS;

  return (
    optimistic.authorUid === server.authorUid &&
    optimistic.body === server.body &&
    optimistic.parentCommentId === server.parentCommentId &&
    optimistic.replyToCommentId === server.replyToCommentId &&
    serverCreatedAtMillis >= earliestMatch &&
    serverCreatedAtMillis <= latestMatch
  );
}
